import { initializeInspector } from "component-inspector";
import type { InspectorHandler } from "component-inspector";
import { useEnvironmentInterface } from "environment-interface";
import { useAddStatusNotification } from "features/notifications/hooks";
import { usePreview } from "features/preview/Feature";
import { usePitcher } from "features/utils/usePitcher";
import { useEditorChannel } from "hooks/pitcher/useEditorChannel";
import React, { useEffect } from "react";
import type { RefObject } from "react";

import { executeVSCodeCommand } from "utils/vscode";

import { suggestCode } from "./boxy";

export type InspectorApi = {
  inspectorEnabled: boolean;
  inspectorAvailable: boolean;
  inspectorLoading: boolean;
  setInspectorEnabled: (
    enabled: boolean,
    pathIgnoreList?: string[],
    isAiPrompt?: boolean,
  ) => void;
};

export const useInspectorApi = (
  previewRef: RefObject<HTMLIFrameElement>,
): InspectorApi => {
  const [preview] = usePreview();
  const pitcher = usePitcher();
  const editorChannel = useEditorChannel();
  const [inspectorApi, setInspectorApi] = React.useState<
    | undefined
    | {
        setInspecting: (
          inspecting: boolean,
          pathIgnoreList?: string[],
          isAiPrompt?: boolean,
        ) => void;
      }
  >();
  const [inspectorEnabled, setInspectorEnabled] = React.useState(false);
  const [inspectorAvailable, setInspectorAvailable] = React.useState(false);
  const [inspectorLoading, setInspectorLoading] = React.useState(true);

  const environment = useEnvironmentInterface();
  const addNotification = useAddStatusNotification();

  useEffect(() => {
    let isDisposed = false;
    let disposer = () => {
      isDisposed = true;
    };
    const handler: InspectorHandler = {
      openFile: async (openFileArgs) => {
        const absolutePath = pitcher.clients.fs.asAbsoluteWorkspacePath(
          openFileArgs.filePath,
        );
        const relativePath =
          pitcher.clients.fs.absoluteToRelativeWorkspacePath(absolutePath);

        let line = openFileArgs.lineNumber;

        if (openFileArgs.filePath.endsWith(".vue")) {
          const fileRef =
            await pitcher.clients.file.openFileByPath(relativePath);
          // This is a vue file, and so we have received the open file information
          // relative to the <template>. We need to find the offset of the themplate.

          const code = fileRef.object.content.toString();
          const lines = code.split("\n");
          const templateLineNumber = lines.findIndex((line) =>
            line.trim().startsWith("<template"),
          );

          if (templateLineNumber > -1) {
            line += templateLineNumber;
          }

          fileRef.dispose();
        }

        editorChannel?.send({
          type: "FOCUS_FILE",
          filepath: relativePath,
          visibleLines: [line, line],
          currentLine: line,
        });

        executeVSCodeCommand("codesandbox.openCsbTab", {
          type: "FILE",
          filepath: relativePath,
          initialSelections: [
            {
              startLineNumber: line,
              endLineNumber: line,
              startColumn: openFileArgs.columnNumber,
              endColumn: openFileArgs.columnNumber + 99999999,
            },
          ],
        });

        if (openFileArgs.suggestCode) {
          suggestCode(
            openFileArgs,
            relativePath,
            pitcher,
            environment,
            () => {
              setInspectorEnabled(false);
              if (inspectorApi) {
                inspectorApi.setInspecting(false);
              }
            },
            (message) => {
              addNotification({ message, status: "warning" });
            },
          );
        }
      },
      onDidInspectorEnabledChange: (enabled) => {
        if (inspectorApi) {
          inspectorApi.setInspecting(enabled);
        }
        setInspectorEnabled(enabled);
      },
      onDidInspectorAvailableChange: (available) => {
        setInspectorAvailable(available);
      },
    };
    if (previewRef.current) {
      initializeInspector(preview.iframeSrc, previewRef.current, handler).then(
        (api) => {
          setInspectorLoading(false);
          if (isDisposed) {
            return;
          }

          disposer = api.dispose;
          setInspectorApi({
            setInspecting: (inspecting, pathIgnoreList, isAiPrompt) => {
              api.setInspectorEnabled(inspecting, pathIgnoreList, isAiPrompt);
            },
          });
        },
      );
    }

    return disposer;
  }, [previewRef, preview.iframeSrc]);

  return {
    inspectorEnabled,
    inspectorAvailable,
    inspectorLoading,
    setInspectorEnabled: (enabled: boolean, pathIgnoreList, isAiPrompt) => {
      if (inspectorApi) {
        inspectorApi.setInspecting(enabled, pathIgnoreList, isAiPrompt);
        setInspectorEnabled(enabled);
      }
    },
  };
};
